import React from "react";
import styled from "@emotion/styled";
import {ColDef, ICellRendererParams, ValueFormatterParams} from "@ag-grid-community/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import VehicleIconResolver from "../../vehicles/VehicleIconResolver";
import {millisecondsToDutation} from "../../../utils/General";
import {DateTimeCellRenderer, getTime, getDate} from "../../common/cell-renderers/DateTimeCellRenderer";
import {DoubleFieldCellValue} from "../../common/cell-renderers/DoubleFieldCellRenderer";
import {RowData} from "../types/types";
import {RoutePlanningDriver} from "../../../providers/RoutePlanningProvider";
import DriverActionMenuCellRenderer from "../components/DriverActionMenuCellRenderer";

export const MapIconButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const buildMasterColumnDefs = (params: {
  appState: {isTimeFormat24hr: boolean};
  isTimezoneOfJobStop: boolean;
  tenantPreferences: any[];
  onToggleRoute: (routeId: string) => void;
  setWorkingRowData: React.Dispatch<React.SetStateAction<RowData[] | null>>;
  selectedDrivers?: RoutePlanningDriver[];
  /** DP-3039: whether any out-of-run driver exists (gates the Change-via-Other guard). */
  hasOtherDrivers?: boolean;
  workingRowDataRef?: React.MutableRefObject<RowData[]>;
  /** DP-3039: open the lifted-out Swap-or-Change dialog (owned by DriverResultList). */
  onOpenSwapChange?: (routeId: string) => void;
  onDeleteRoute?: (routeId: string) => void;
  /** DP-3175: show star icon for existing manifest routes (when "maintain existing work" selected). */
  showManifestStar?: boolean;
  /** DP-3175: manifest driver IDs left untouched by integrity fallback — show info icon. */
  untouchedManifestDriverIds?: number[];
}): ColDef<RowData>[] => {
  const {
    appState,
    isTimezoneOfJobStop,
    tenantPreferences,
    onToggleRoute,
    setWorkingRowData,
    selectedDrivers,
    hasOtherDrivers,
    workingRowDataRef,
    onOpenSwapChange,
    onDeleteRoute,
    showManifestStar,
    untouchedManifestDriverIds
  } = params;
  return [
    {
      field: "name",
      headerName: "Driver",
      flex: 1,
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams:
        onOpenSwapChange && onDeleteRoute && selectedDrivers
          ? {
              innerRenderer: DriverActionMenuCellRenderer,
              innerRendererParams: {
                selectedDrivers,
                hasOtherDrivers,
                workingRowDataRef,
                onOpenSwapChange,
                onDeleteRoute,
                showManifestStar,
                untouchedManifestDriverIds
              }
            }
          : undefined,
      pinned: "left",
      lockPosition: "left",
      lockPinned: true,
      suppressMovable: true
    },
    {field: "driverCode", initialWidth: 80, headerName: "Driver Code"},
    {headerName: "Stops", initialWidth: 60, valueGetter: (p) => p.data?.assignedStops?.length ?? 0},
    {
      headerName: "End Time",
      initialWidth: 120,
      cellRenderer: DateTimeCellRenderer,
      valueGetter: (p) => {
        const endTime = p.data?.endTime;
        const endTimeZone = p.data?.endTimeZone;
        if (!endTime || !endTimeZone) return null;
        const time = getTime(endTime.toISOString(), endTimeZone, isTimezoneOfJobStop, appState.isTimeFormat24hr);
        const date = getDate(endTime.toISOString(), endTimeZone, isTimezoneOfJobStop);
        return {
          upperField: {value: time},
          lowerField: {value: date, style: {color: "#6B7280"}}
        } as DoubleFieldCellValue;
      }
    },
    {
      field: "duration",
      headerName: "Duration",
      initialWidth: 100,
      valueFormatter: (p: ValueFormatterParams<RowData>) => {
        const duration = p.value;
        return duration ? millisecondsToDutation(duration) : "";
      }
    },
    {
      field: "miles",
      headerName: "Miles",
      initialWidth: 80,
      valueFormatter: (p: ValueFormatterParams<RowData>) => (p.value ? String(p.value) : "")
    },
    {
      field: "vehicleDescription",
      headerName: "Vehicle",
      initialWidth: 100,
      cellRenderer: (p: ICellRendererParams<RowData, string>) => {
        const vehicleDescription = p.value;
        return (
          <FontAwesomeIcon
            data-testid={"vehicle-icon"}
            size={"lg"}
            color={"#797979"}
            icon={VehicleIconResolver(vehicleDescription, tenantPreferences)}
            title={vehicleDescription}
            style={{margin: "auto"}}
          />
        );
      }
    },
    {
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: "right",
      resizable: false,
      suppressSizeToFit: true,
      cellStyle: {justifyContent: "center"},
      cellRenderer: (p: ICellRendererParams<RowData>) => {
        const routeId = p.data?.routeId;
        const isVisible = p.data?.isVisible ?? true;
        const routeColor = p.data?.routeColor ?? "#4B5563";
        return (
          <MapIconButton
            onClick={(e) => {
              e.stopPropagation();
              if (!routeId) return;
              setWorkingRowData(
                (prev) =>
                  prev?.map((row) => (row.routeId === routeId ? {...row, isVisible: !row.isVisible} : row)) ?? null
              );
              onToggleRoute(routeId);
            }}
          >
            <FontAwesomeIcon
              data-testid={"map-icon"}
              color={isVisible ? routeColor : "#9CA3AF"}
              icon={isVisible ? solid("location-check") : solid("location-xmark")}
            />
          </MapIconButton>
        );
      }
    }
  ];
};
